import produce from 'immer';
import { registerReducer } from '../../../StateSetup/RootReducer';

const { BFF_URL, GBCORE_CLIENT_NAME, GBCORE_CLIENT_VERSION } = window.__ENV__;

// Action names
const ACTION_NAME_PREFIX = 'action/updateMemberBankAccount';
export const REQUEST_ACTION_NAME = `${ACTION_NAME_PREFIX}/request`;
export const RESPONSE_ACTION_NAME = `${ACTION_NAME_PREFIX}/response`;
export const ERROR_ACTION_NAME = `${ACTION_NAME_PREFIX}/error`;

/**
 * Updates bank information for a member.
 * @param {object} logger
 * @returns {Promise<object>}
 */
export const UpdateMemberBankAccount = (branchNumber, institutionNumber, accountNumber) => {
    return async (dispatch, getState, { logger }) => {
        dispatch({ type: REQUEST_ACTION_NAME });

        const { jwt, sessionCorrelationId, language } = getState().Authentication;
        const memberBankAccount = getState().MemberData.member.bankAccount;
        const transactionId = logger.generateGuid();

        logger.info(`${ACTION_NAME_PREFIX}: Updating member bank account.`, { correlation: { transactionId } });

        let bankAccountId;

        if (memberBankAccount) {
            const updateMemberBankAccountMutation = await fetch(`${BFF_URL}/api/v2`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'gb-authorization': jwt,
                    'mli-session-correlation-id': sessionCorrelationId,
                    'mli-correlation-id': transactionId,
                    'mli-channel': 'web',
                    'gb-client-name': GBCORE_CLIENT_NAME,
                    'gb-client-version': GBCORE_CLIENT_VERSION,
                    'Accept-Language': language,
                },
                body: JSON.stringify({
                    operationName: 'UpdateMemberBankAccountMutation',
                    variables: {
                        bankAccount: {
                            bankAccountId: memberBankAccount.bankAccountId,
                            status: memberBankAccount.status,
                            usage: memberBankAccount.usage,
                            institutionNumber,
                            branchNumber,
                            accountNumber,
                        },
                    },
                    query: `
                    mutation UpdateMemberBankAccountMutation($bankAccount: BankAccountInput) {
                        updateMemberBankAccount(bankAccount: $bankAccount){
                            bankAccountId
                        }
                    }
                `,
                }),
            });
            const updateMemberBankAccountMutationResponse = await updateMemberBankAccountMutation.json();

            if (updateMemberBankAccountMutationResponse.errors) {
                const { message, code } = updateMemberBankAccountMutationResponse.errors[0];
                logger.error(
                    `${ACTION_NAME_PREFIX}: Failure when updating member bank account. Code: ${code} Reason: ${message}`,
                    { correlation: { transactionId } }
                );
                return dispatch({
                    type: ERROR_ACTION_NAME,
                    code,
                    message,
                });
            }
            bankAccountId = updateMemberBankAccountMutationResponse.data.updateMemberBankAccount.bankAccountId;
            return dispatch({
                type: RESPONSE_ACTION_NAME,
                success: true,
                bankAccountId,
                branchNumber,
                institutionNumber,
                accountNumber,
            });
        }
        logger.info(`${ACTION_NAME_PREFIX}: Creating member bank account.`, { correlation: { transactionId } });

        const createMemberBankAccountMutation = await fetch(`${BFF_URL}/api/v2`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'gb-authorization': jwt,
                'mli-session-correlation-id': sessionCorrelationId,
                'mli-correlation-id': transactionId,
                'mli-channel': 'web',
                'gb-client-name': GBCORE_CLIENT_NAME,
                'gb-client-version': GBCORE_CLIENT_VERSION,
                'Accept-Language': language,
            },
            body: JSON.stringify({
                operationName: 'CreateMemberBankAccountMutation',
                variables: {
                    bankAccount: {
                        institutionNumber,
                        branchNumber,
                        accountNumber,
                    },
                },
                query: `
                    mutation CreateMemberBankAccountMutation($bankAccount: BankAccountInput) {
                        createMemberBankAccount(bankAccount: $bankAccount){
                            bankAccountId
                        }
                    }
                `,
            }),
        });
        const createMemberBankAccountMutationResponse = await createMemberBankAccountMutation.json();

        if (createMemberBankAccountMutationResponse.errors) {
            const { message, code } = createMemberBankAccountMutationResponse.errors[0];
            logger.error(
                `${ACTION_NAME_PREFIX}: Failure when creating member bank account. Code: ${code} Reason: ${message}`,
                { correlation: { transactionId } }
            );
            return dispatch({
                type: ERROR_ACTION_NAME,
                code,
                message,
            });
        }
        bankAccountId = createMemberBankAccountMutationResponse.data.createMemberBankAccount.bankAccountId;
        return dispatch({
            type: RESPONSE_ACTION_NAME,
            success: true,
            bankAccountId,
            branchNumber,
            institutionNumber,
            accountNumber,
        });
    };
};

export const requestReducer = produce((state) => {
    state.BankData.isSavingBankInformation = true;
});

/**
 * Handles a successful response when a user's bank account has been updated
 * @param {object} state
 * @param {object} action
 */
export const responseReducer = produce((state, action) => {
    state.BankData.isSavingBankInformation = false;
    state.BankData.isBankValid = false;
    // If the member does not have a bank account in MC, state.MemberData.member.bankAccount will be null.
    // It is important to check for that and initialize the bankAccount property on the member object in
    // case we are adding a bank account for the member for the very first time.
    if (!state.MemberData.member.bankAccount) {
        state.MemberData.member.bankAccount = {};
    }
    state.MemberData.member.bankAccount.bankAccountId = action.bankAccountId;
    state.MemberData.member.bankAccount.branchNumber = action.branchNumber;
    state.MemberData.member.bankAccount.institutionNumber = action.institutionNumber;
    state.MemberData.member.bankAccount.accountNumber = action.accountNumber;
    state.MemberData.member.bankAccount.bankName = state.BankData.bankName;
});

/**
 * Fired off when the request encouters an error.
 * @param {object} state
 * @param {object} action
 */
export const errorReducer = produce((state, action) => {
    state.BankData.isSavingBankInformation = false;
    state.Errors.bankDataUpdate.hasError = true;
    state.Errors.bankDataUpdate.code = action.code;
    state.Errors.bankDataUpdate.message = action.message;
});

registerReducer(REQUEST_ACTION_NAME, requestReducer);
registerReducer(RESPONSE_ACTION_NAME, responseReducer);
registerReducer(ERROR_ACTION_NAME, errorReducer);
